import React, {useState, useMemo} from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {SeoHead} from "./SeoHead";
import {usePublishedInstructors} from "../firebase/usePublishedInstructors";
import {InstructorsMap} from "../instructors/InstructorsMap";
import {InstructorsList} from "../instructors/InstructorsList";
import {InstructorFilterBar} from "../instructors/InstructorFilterBar";
import {extractCountiesWithCount, filterInstructors} from "../instructors/filterInstructors";
import {hasActiveUpcomingCourses} from "../instructors/courseValidation";
import {CourseInterestModal} from "../instructors/CourseInterestModal";

export const ForRegularPersons = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("Alle");
    const [onlyWithUpcomingCourses, setOnlyWithUpcomingCourses] = useState(false);
    const [selectedInstructorId, setSelectedInstructorId] = useState(null);
    const [targetCoordinates, setTargetCoordinates] = useState(null);
    const [interestModalInstructor, setInterestModalInstructor] = useState(null);
    const [interestModalCourse, setInterestModalCourse] = useState(null);

    const rawInstructors = usePublishedInstructors({});
    const isLoading = !rawInstructors || (rawInstructors.length === 0 && !searchTerm && selectedCounty === "Alle");

    const counties = useMemo(() => {
        return extractCountiesWithCount(rawInstructors);
    }, [rawInstructors]);

    const upcomingCoursesCount = useMemo(() => {
        return (rawInstructors || []).filter(hasActiveUpcomingCourses).length;
    }, [rawInstructors]);

    const filteredInstructors = useMemo(() => {
        return filterInstructors(rawInstructors, {
            searchTerm,
            selectedCounty,
            onlyWithUpcomingCourses,
        });
    }, [rawInstructors, searchTerm, selectedCounty, onlyWithUpcomingCourses]);

    const handleLocate = instructor => {
        if (instructor && typeof instructor.lat === "number" && typeof instructor.lon === "number") {
            setSelectedInstructorId(instructor.id);
            setTargetCoordinates([instructor.lat, instructor.lon]);
            if (!isDesktop) {
                setActiveTab(1); // Switch to map tab on mobile
            }
        }
    };

    const handleResetFilters = () => {
        setSearchTerm("");
        setSelectedCounty("Alle");
        setOnlyWithUpcomingCourses(false);
    };

    const handleExpressInterest = (instructor, course) => {
        setInterestModalInstructor(instructor);
        setInterestModalCourse(course || null);
    };

    return (
        <Box sx={{ pb: 6, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <SeoHead
                title="Finn kursholder"
                description="Finn autoriserte og sertifiserte kursholdere i bekymringsmestring i din kommune eller bydel."
                path="/find-instructor"
            />

            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: 700, color: "primary.main" }}
                >
                    Finn sertifisert kursholder
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, lineHeight: 1.7 }}>
                    Her finner du en oversikt over sertifiserte kursholdere i bekymringsmestring som har
                    registrert seg i vårt register. Dersom du ikke finner noen i din umiddelbare nærhet, kan det
                    likevel lønne seg å kontakte din hjemkommune eller fastlege for å høre om kurset tilbys lokalt.
                </Typography>
            </Box>

            <InstructorFilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                counties={counties}
                selectedCounty={selectedCounty}
                onSelectCounty={setSelectedCounty}
                onlyWithUpcomingCourses={onlyWithUpcomingCourses}
                onToggleUpcomingCourses={() => setOnlyWithUpcomingCourses((prev) => !prev)}
                upcomingCoursesCount={upcomingCoursesCount}
                resultCount={filteredInstructors.length}
            />

            {isDesktop ? (
                /* Desktop Split Screen: Left cards, Right sticky map */
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", width: "100%" }}>
                    <Box sx={{ flex: "1 1 54%", minWidth: 0 }}>
                        <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {`Viser ${filteredInstructors.length} ${
                                    filteredInstructors.length === 1 ? "kursholder" : "kursholdere"
                                }`}
                            </Typography>
                        </Box>
                        <InstructorsList
                            instructors={filteredInstructors}
                            isLoading={isLoading && rawInstructors.length === 0}
                            onLocate={handleLocate}
                            selectedInstructorId={selectedInstructorId}
                            onResetFilters={handleResetFilters}
                            onExpressInterest={handleExpressInterest}
                        />
                    </Box>

                    <Box
                        sx={{
                            flex: "1 1 46%",
                            minWidth: 0,
                            position: "sticky",
                            top: 84,
                            alignSelf: "flex-start",
                            height: "calc(100vh - 108px)",
                            minHeight: 520,
                            borderRadius: 3,
                            overflow: "hidden",
                            border: "1px solid rgba(40, 82, 56, 0.12)",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                        }}
                    >
                        <InstructorsMap
                            instructors={filteredInstructors}
                            targetCoordinates={targetCoordinates}
                            height="100%"
                            minHeight={520}
                            onExpressInterest={handleExpressInterest}
                        />
                    </Box>
                </Box>
            ) : (
                    /* Mobile / Small Screens: Tabs between List & Map */
                    <Box>
                        <Tabs
                            value={activeTab}
                            indicatorColor="primary"
                            textColor="primary"
                            aria-label="Velg liste eller kart over kursholdere"
                            onChange={(event, value) => setActiveTab(value)}
                            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                        >
                            <Tab
                                label={`Liste (${filteredInstructors.length})`}
                                id="tab-list"
                                aria-controls="tabpanel-list"
                                sx={{
                                    fontWeight: 600,
                                    minHeight: 48,
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: "-2px",
                                    },
                                }}
                            />
                            <Tab
                                label={`Kart (${filteredInstructors.length})`}
                                id="tab-map"
                                aria-controls="tabpanel-map"
                                sx={{
                                    fontWeight: 600,
                                    minHeight: 48,
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: "-2px",
                                    },
                                }}
                            />
                        </Tabs>

                        <Box
                            role="tabpanel"
                            id={activeTab === 0 ? "tabpanel-list" : "tabpanel-map"}
                            aria-labelledby={activeTab === 0 ? "tab-list" : "tab-map"}
                        >
                            {activeTab === 0 ? (
                                <InstructorsList
                                    instructors={filteredInstructors}
                                    isLoading={isLoading && rawInstructors.length === 0}
                                    onLocate={handleLocate}
                                    selectedInstructorId={selectedInstructorId}
                                    onResetFilters={handleResetFilters}
                                    onExpressInterest={handleExpressInterest}
                                />
                            ) : (
                                <InstructorsMap
                                    instructors={filteredInstructors}
                                    targetCoordinates={targetCoordinates}
                                    height="60vh"
                                    minHeight={380}
                                    onExpressInterest={handleExpressInterest}
                                />
                            )}
                        </Box>
                    </Box>
                )}

            <CourseInterestModal
                open={Boolean(interestModalInstructor)}
                instructor={interestModalInstructor}
                selectedCourse={interestModalCourse}
                onClose={() => {
                    setInterestModalInstructor(null);
                    setInterestModalCourse(null);
                }}
            />
        </Box>
    );
};